import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { first } from 'lodash';

import IMG_ARROW from 'assets/images/arrow-dropdown.png';
import Icon from 'components/generic/Icon.component';
import useInputHelpers from 'hooks/useInputHelpers.hook';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useTheme } from 'providers/Theme.provider';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import deRef from 'utils/objects/deRef.util';
import canonicalizeSelectOptions from 'utils/react/canonicalizeSelectOptions.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import CN from './DropdownInput.module.scss';

const CTAG = 'DropdownInput';
const L = logger(CTAG); // eslint-disable-line no-unused-vars

const DropdownInput = ({
  align,
  name,
  onChange = alwaysNoop,
  disabled,
  options: optionsFromProps,
  value: valueFromProps,
  tag: tagFromProps,
  icon,
  variant,
}) => {
  const { isDarkMode } = useTheme();

  const { val: isListRendered, tip: toggle, nay: close } = useTip(false);
  const selectRef = useRef(null);
  const selectedItemRef = useRef(null);

  const parser = ($) => ({ raw: first(asObject($).extra) });
  const { attributes, value: selected, handle } = useInputHelpers(parser, valueFromProps, name, tagFromProps);

  // transform whatever comes from outside into an array of objects which shape we know of
  const options = canonicalizeSelectOptions(optionsFromProps);

  useOnClickOutside(selectRef, close);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    toggle();
  }, [disabled, toggle]);

  const buttonClasses = cn({
    [CN.selectButton]: true,
    [CN.isOpen]: isListRendered,
  });

  const dropdownClasses = cn({
    [CN.list]: true,
    [CN[`${align}Align`]]: align,
  });

  const displayValue =
    selected?.description === valueFromProps?.description
      ? selected?.description
      : valueFromProps?.description || first(options)?.description || '';

  useEffect(() => {
    if (isListRendered && selectedItemRef.current)
      elementScrollTo({ element: deRef(selectedItemRef), behavior: 'smooth', block: 'center' });
  }, [isListRendered]);

  // TODO: fix div role="button" when global button css is removed
  return (
    <div
      ref={selectRef}
      {...attributes}
      data-bem="DropdownInput"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
        [CN[`${variant}Variant`]]: variant,
      })}
    >
      <div
        data-bem="DropdownInput__button"
        className={buttonClasses}
        role="button"
        tabIndex={0}
        disabled={disabled}
        onClick={handleToggle}
      >
        {icon}
        {displayValue}
        <img className={CN.dropdownArrow} src={IMG_ARROW} alt="Arrow Icon" />
      </div>

      {isListRendered && (
        <div data-bem="DropdownInput__list" className={dropdownClasses}>
          {options.map((item, index) => {
            const isSelected = item.description === selected?.description;

            const handleChange = (ev) => {
              handle(`${CTAG}.onChange`, onChange, ev, item);
              close();
            };

            return (
              <div
                data-bem="DropdownInput__item"
                key={createNodeKey({ tag: 'item', index })}
                ref={isSelected ? selectedItemRef : null}
                className={cn({
                  [CN.item]: true,
                  [CN.selectedItem]: isSelected,
                })}
                role="button"
                tabIndex={0}
                onClick={handleChange}
              >
                {item.description || ''}
                {isSelected && <Icon variant="tick" width="sm" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DropdownInput;
